import React, { useEffect, useState } from "react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_TEMPLATE = `<!doctype html><html><body style="margin:0;padding:0;background:#f7f5f0;font-family:Georgia,serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="padding:40px 12px;">
  <tr><td align="center">
    <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border:1px solid #0a192f1a;">
      <tr><td style="background:#0a192f;padding:28px 32px;color:#efeade;">
        <div style="font-size:11px;letter-spacing:0.28em;text-transform:uppercase;color:#b08d57;">Décryptage mensuel · Phénix</div>
        <div style="font-size:26px;margin-top:10px;">Titre du décryptage</div>
      </td></tr>
      <tr><td style="padding:32px;color:#0f172a;font-size:15px;line-height:1.7;">
        <p>Votre contenu ici…</p>
      </td></tr>
      <tr><td style="background:#efeade;padding:14px 32px;font-size:11px;color:#475569;">
        30 avenue Maréchal Foch, 69006 Lyon · ORIAS 26005580
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;

export default function AdminNewsletterSend() {
  const { authFetch } = useAdminAuth();
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(DEFAULT_TEMPLATE);
  const [previewTo, setPreviewTo] = useState("");
  const [subscribersCount, setSubscribersCount] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    authFetch("get", "/admin/subscribers")
      .then((res) => setSubscribersCount(res.data.count))
      .catch(() => setSubscribersCount(0));
  }, [authFetch]);

  const send = async (isPreview) => {
    if (!subject.trim() || !html.trim()) {
      toast.error("Merci de renseigner le sujet et le contenu HTML.");
      return;
    }
    if (isPreview && !previewTo.trim()) {
      toast.error("Renseignez une adresse pour la prévisualisation.");
      return;
    }
    if (!isPreview && !window.confirm(`Envoyer à ${subscribersCount ?? "?"} abonnés ?`)) return;

    setSending(true);
    try {
      const payload = { subject, html };
      if (isPreview) payload.preview_only_to = previewTo.trim();
      const res = await authFetch("post", "/admin/newsletter/send", payload);
      toast.success(
        `Envoyé — ${res.data.sent}/${res.data.total} email(s). ${res.data.failed.length ? "Échecs : " + res.data.failed.length : ""}`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 503) {
        toast.error("Resend n'est pas configuré. Ajoutez RESEND_API_KEY.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur d'envoi.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-broadcast-root">
      <div className="flex items-center gap-2 mb-3">
        <Send size={16} strokeWidth={1.5} className="text-[#b08d57]" />
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#b08d57]">
          Décryptage mensuel
        </span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl mb-3">Envoyer une newsletter</h1>
      <p className="text-[#efeade]/60 mb-10 text-sm">
        {subscribersCount == null
          ? "Chargement…"
          : `${subscribersCount} abonné${subscribersCount > 1 ? "s" : ""} destinataire${subscribersCount > 1 ? "s" : ""}.`}
      </p>

      <div className="mb-8 p-5 border border-[#f0b74e]/30 bg-[#f0b74e]/5 flex items-start gap-3">
        <AlertTriangle size={16} strokeWidth={1.5} className="text-[#f0b74e] mt-0.5 shrink-0" />
        <div className="text-xs text-[#efeade]/80 leading-relaxed">
          En mode test Resend, seules les adresses de votre compte peuvent recevoir les emails.
          Vérifiez votre domaine <span className="text-[#f0b74e]">phenix-assurances.com</span> dans le dashboard Resend pour envoyer aux vrais abonnés.
        </div>
      </div>

      <div className="space-y-5 max-w-3xl">
        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57] block mb-2">
            Sujet <span className="text-[#c05c3d]">*</span>
          </span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="admin-broadcast-subject"
            className={inputCls}
            placeholder="ex. Décryptage · Réforme prévoyance 2026"
          />
        </label>

        <label className="block">
          <span className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57] block mb-2">
            Contenu HTML <span className="text-[#c05c3d]">*</span>
          </span>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            data-testid="admin-broadcast-html"
            rows={16}
            className={inputCls + " font-mono text-xs resize-y"}
          />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#efeade]/10 pt-6">
          <div>
            <label className="block mb-2">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#b08d57]">
                Prévisualiser (envoyer à une seule adresse)
              </span>
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={previewTo}
                onChange={(e) => setPreviewTo(e.target.value)}
                data-testid="admin-broadcast-preview-email"
                className={inputCls}
                placeholder="votre@email.fr"
              />
              <button
                onClick={() => send(true)}
                disabled={sending}
                data-testid="admin-broadcast-preview-send"
                className="shrink-0 border border-[#b08d57]/60 text-[#f0b74e] px-4 text-xs tracking-[0.22em] uppercase hover:bg-[#b08d57] hover:text-[#0a192f] transition-colors disabled:opacity-50"
              >
                Prévisualiser
              </button>
            </div>
          </div>
          <div className="flex items-end justify-end">
            <button
              onClick={() => send(false)}
              disabled={sending || !subscribersCount}
              data-testid="admin-broadcast-send"
              className="inline-flex items-center gap-2 bg-[#c05c3d] text-[#efeade] px-6 py-3 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#a34a2f] transition-colors disabled:opacity-50"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Envoyer à tous les abonnés
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-[#efeade]/25 focus:border-[#b08d57] focus:outline-none px-4 py-3 text-sm text-[#efeade] placeholder:text-[#efeade]/30 transition-colors";
